import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';
import { UpgradePrompt } from '@/components/Analysis/shared/UpgradePrompt';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { CustomTextField } from '@/components/shared/CustomField';
import { GenericAccordion } from '@/components/shared/GenericAccordion';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { abyssColors } from '@/theme/colors';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { Box, Link, Stack, SxProps, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { FieldViewer } from '../../FieldViewer';
import { useTriggerPoiEditing } from '../../hooks';
import { convertTemplateDataIntoFieldsArray } from '../../utils';
import { DetailsOptionMenu } from './DetailsOptionMenu';

type ShareProps = {
  pointOfInterest: PointOfInterest;
  template?: PointOfInterestTemplate;
};

type AitUserVerifiedPoiProps = {
  fields: Array<{ label: string; value: string }>;
  disableEdit?: boolean;
} & ShareProps;

export const AitUserVerifiedPoiView = ({
  fields,
  pointOfInterest,
  disableEdit,
}: AitUserVerifiedPoiProps) => {
  return (
    <Box sx={{ px: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
        <Typography variant="h6">Pre Inspection Data</Typography>
        {disableEdit || <DetailsOptionMenu selectedPoiInfo={pointOfInterest} />}
      </Stack>
      <FieldViewer fields={fields} selectedPoiInfo={pointOfInterest} />
    </Box>
  );
};

type InspectorUnverifiedPoiProps = {} & ShareProps;

const InspectorUnverifiedPoiView = ({ pointOfInterest, template }: InspectorUnverifiedPoiProps) => {
  const { triggerEdit } = useTriggerPoiEditing();

  const onClick = useCallback(() => {
    triggerEdit({
      formState: 'Verify',
      pointOfInterest,
      template,
    });
  }, [pointOfInterest, template, triggerEdit]);

  return (
    <>
      <Typography>Data awaits verification</Typography>
      <Link component="button" onClick={onClick} mx={1}>
        Click here
      </Link>
      <Typography>to verify the data</Typography>
    </>
  );
};

type InspectorVerifiedPoiViewProps = {
  pointOfInterest: PointOfInterest;
  template: PointOfInterestTemplate;
  fields: Array<{ label: string; value: string }>;
  disableEdit?: boolean;
};
export const InspectorVerifiedPoiView = ({
  pointOfInterest,
  template,
  fields,
  disableEdit,
}: InspectorVerifiedPoiViewProps) => {
  const unitSystem = useAtomValue(unitSystemState);

  // Use the updated fields with verified data
  const verifiedFields = template?.fields
    ? convertTemplateDataIntoFieldsArray({
        selectedPoi: pointOfInterest,
        templateFields: template.fields,
        unitSystem,
        isVerified: true, // Use this flag to get verified fields
      })
    : fields;

  return (
    <Box sx={{ px: 3 }} data-testid="Post Inspection Data-accordian">
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
        <Typography fontSize="1.6rem" fontWeight={800}>
          Post Inspection Data
        </Typography>
        {disableEdit || <DetailsOptionMenu selectedPoiInfo={pointOfInterest} />}
      </Stack>
      <FieldViewer fields={verifiedFields} sx={{ pb: 2 }} selectedPoiInfo={pointOfInterest} />
      <CustomTextField
        key="verified-by"
        mode="ReadOnly"
        id="verified-by"
        title="Verified By"
        value={pointOfInterest.verifiedByUser?.email ?? NOT_APPLICABLE_LABEL}
        sx={{ color: abyssColors.primary[400] }}
      />
    </Box>
  );
};

type GenericAccordionProps = {
  fields: Array<{ label: string; value: string }>;
  accordianProps?: SxProps;
  template: PointOfInterestTemplate;
  pointOfInterest: PointOfInterest;
  canVerifyData: boolean;
};

type VerifiedFieldsAccordianProps = {
  canVerifyData: boolean;
} & GenericAccordionProps;
const VerifiedFieldsAccordian = ({
  fields,
  accordianProps,
  pointOfInterest,
  template,
  canVerifyData,
}: VerifiedFieldsAccordianProps) => {
  const unitSystem = useAtomValue(unitSystemState);

  // Use the updated fields with verified data if the POI is verified
  const verifiedFields =
    pointOfInterest.isVerified && template?.fields
      ? convertTemplateDataIntoFieldsArray({
          selectedPoi: pointOfInterest,
          templateFields: template.fields,
          unitSystem,
          isVerified: true, // Use this flag to get verified fields
        })
      : fields;

  return (
    <GenericAccordion
      summary="Post Inspection Data"
      summaryStyles={{ fontWeight: 500 }}
      openByDefault
      data-testid="verified-fields-accordian"
      {...accordianProps}
    >
      <Stack alignItems="center">
        {(() => {
          if (pointOfInterest.isVerified) {
            return (
              <FieldViewer
                sx={{ width: '100%' }}
                fields={verifiedFields}
                selectedPoiInfo={pointOfInterest}
              />
            );
          } else if (canVerifyData) {
            return (
              <Box sx={{ width: '70%', textAlign: 'center' }}>
                <InspectorUnverifiedPoiView pointOfInterest={pointOfInterest} template={template} />
              </Box>
            );
          } else {
            return (
              <Stack alignItems="center" my={2}>
                <FindInPageIcon fontSize="large" />
                <Typography fontWeight={400}>
                  Data awaits verification from API Inspector
                </Typography>
              </Stack>
            );
          }
        })()}
      </Stack>
    </GenericAccordion>
  );
};

export const InspectorVerifiedFieldsAccordian = ({
  fields,
  accordianProps,
  pointOfInterest,
  template,
  canVerifyData,
}: GenericAccordionProps) => (
  <UpgradePrompt
    feature="inspectionServices"
    subFeature="VERIFICATION"
    featureTitle="Field Verification Data"
    paragraphs={[
      'Upgrading will allow you to verify your POIs and complete your workflow in Abyss Fabric without limitations.',
    ]}
  >
    <VerifiedFieldsAccordian
      fields={fields}
      accordianProps={accordianProps}
      template={template}
      pointOfInterest={pointOfInterest}
      canVerifyData={canVerifyData}
    />
  </UpgradePrompt>
);
